// @module /utilities/stores/index.ts
import { defineStore } from 'pinia';
import { reactive, ref, type Ref, type UnwrapRef, watch } from 'vue';
import dayjs from 'dayjs';
import { useEventBus } from '@vueuse/core';
import type { AsyncData } from 'nuxt/app';
import {
  MODAL_DELETE,
  MODAL_SEND_REMINDERS,
  MODAL_TYPES,
  MODAL_VOID,
} from '../constants';
import utilitiesApi from '../services/utilitiesApi';
import {
  FormStatus,
  type FormId,
  type UtilitiesForm,
  type UtilitiesQuery,
  type UtilitiesStats,
} from '../types';
import { useRouter, useZiggy, unref, navigateTo } from '#imports';
import type { LocationQueryValue } from 'vue-router';
import type { FormUploadPayload } from '@utilities/types/requests';
import { utilitiesKey } from '@utilities/services/events';
import { useHelpers } from '~/composables/useHelpers';
import { useApiFetchBlob } from '~/modules/support/fetch/runtime/composables/useApiFetchBlob';

const dateFormat = 'DD/MM/YY';
const updatedFromSalesforce: Ref<UnwrapRef<boolean>> = ref(false);

const validStatusFilters = new Set<string>([
  FormStatus.DRAFT,
  FormStatus.SENT,
  FormStatus.PENDING,
  FormStatus.PROCESSING,
  FormStatus.APPROVED,
  FormStatus.ACTIVE,
  FormStatus.CANCELLED,
  FormStatus.DECLINED,
]);

const normalizeStatusFilter = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string => {
  let status: string | undefined;

  if (Array.isArray(value)) {
    status = value.find(
      (item): item is string =>
        typeof item === 'string' && item !== '' && item !== 'null',
    );
  } else if (typeof value === 'string') {
    status = value.includes(',')
      ? value.split(',').find((item) => item && item !== '' && item !== 'null')
      : value;
  }

  if (!status || status === 'null') {
    return '';
  }

  return validStatusFilters.has(status) ? status : '';
};

const setAddressString = (data: UtilitiesForm) => {
  if (typeof data === 'undefined' || typeof data.address === 'undefined') {
    return data;
  }

  data.addressString = '';

  if (data.address.line1) {
    data.addressString = data.address.line1.trim() + ', ';
  }

  if (data.address.city) {
    data.addressString += data.address.city.trim() + ', ';
  }

  if (data.address.postcode) {
    data.addressString += `${data.address.postcode}`;
  }

  return data;
};

const setDateStrings = (data: UtilitiesForm) => {
  data.createdAtString = data.createdAt
    ? dayjs(data.createdAt).format(dateFormat)
    : '';
  data.updatedAtString = data.updatedAt
    ? dayjs(data.updatedAt).format(dateFormat)
    : '';
  data.endDateString = data.endDate
    ? dayjs(data.endDate).format(dateFormat)
    : '';
  data.startDateString = data.startDate
    ? dayjs(data.startDate).format(dateFormat)
    : '';
  return data;
};

const mapFormData = (form: UtilitiesForm) => {
  form = setDateStrings(form);

  if (form.address && form.address.street) {
    const streetComponents = form.address.street.split(' ');
    form.address.houseNo = streetComponents.shift();
    form.address.street = streetComponents.join(' ');
  }

  form = setAddressString(form);

  if (form.source?.value === 'utility_account') {
    form.online = true;
  }

  if (Array.isArray(form.tenants)) {
    form.tenantsSigned = form.tenants
      .map((tenant) => tenant.signed)
      .filter(Boolean).length;
  }

  form.tenantCount = form.tenants.length;
  form.signaturesRemaining = form.tenantCount - form.tenantsSigned;

  return form;
};

export const useUtilityStore = defineStore('utilities', () => {
  const { updateRouteQueryParams } = useHelpers();
  const { user } = useAuth();
  const bus = useEventBus(utilitiesKey);
  const router = useRouter();
  const isInitialLoad = ref(true);
  const route = useRoute();

  const defaultQuery = computed(() => {
    const routeIds = route.query['filter[ids][]']; // Only check the correct format
    let idsArray = null;

    if (routeIds) {
      if (Array.isArray(routeIds)) {
        idsArray = routeIds.filter((id) => id && id !== '' && id !== 'null');
      } else if (routeIds !== '' && routeIds !== 'null') {
        idsArray = [routeIds];
      }
    }

    return {
      filter: {
        from: route.query['filter[from]'] ?? '',
        search: route.query['filter[search]'] ?? '',
        status: normalizeStatusFilter(route.query['filter[status]']),
        to: route.query['filter[to]'] ?? '',
        ids: idsArray,
        updated_at: route.query['filter[updated_at]'] ?? '',
        has_holding_agreement:
          route.query['filter[has_holding_agreement]'] ?? null,
        is_signed:
          route.query['filter[is_signed]'] === 'true'
            ? true
            : route.query['filter[is_signed]'] === 'false'
              ? false
              : null,
      },
      page: 1,
      perPage: 15,
      sort: '-created_at',
    };
  });

  const defaultNeedsAttentionQuery = {
    filter: {
      from: '',
      search: '',
      ids: null,
      updated_at: null,
      has_holding_agreement: null,
    },
    page: 1,
    perPage: 5,
    sort: '-created_at',
  };

  const defaultStats = {
    draft: 0,
    sent: 0,
    pending: 0,
    processing: 0,
    approved: 0,
    active: 0,
    cancelled: 0,
    declined: 0,
    total: 0,
  };

  const stats = reactive<UtilitiesStats>({ ...defaultStats });
  const query = reactive<UtilitiesQuery>({ ...unref(defaultQuery) });
  const needsAttentionQuery = reactive<UtilitiesQuery>({
    ...defaultNeedsAttentionQuery,
  });

  const loading = reactive({
    stats: false,
    table: false,
  });

  const needsAttentionLoading = reactive({
    stats: false,
    table: false,
  });

  const forms = ref<UtilitiesForm[]>([]);
  const needsAttentionForms = ref<UtilitiesForm[]>([]);

  const meta = reactive({
    currentPage: 0,
    totalPages: 0,
    lettingSeasons: [],
    total: 0,
  });

  const needsAttentionMeta = reactive({
    currentPage: 0,
    totalPages: 0,
    lettingSeasons: [],
    total: 0,
  });

  const modal = ref<{ open: string; data?: object | null }>({
    open: '',
    data: {},
  });

  // Getters
  const queryParams = computed(() => {
    const params: Record<string, any> = {
      'filter[from]': query.filter.from,
      'filter[search]': query.filter.search,
      'filter[status]': normalizeStatusFilter(query.filter.status),
      'filter[to]': query.filter.to,
      'filter[has_holding_agreement]': query.filter.has_holding_agreement,
      page: query.page,
      perPage: query.perPage,
      sort: query.sort,
    };

    // Only add ids if it has values
    if (query.filter.ids && query.filter.ids.length > 0) {
      params['filter[ids][]'] = query.filter.ids;
    }

    return params;
  });

  const needsAttentionQueryParams = computed(() => {
    const now = dayjs();
    const thisOctober = dayjs().set('month', 9).startOf('month');
    const lastOctober = thisOctober.subtract(1, 'year');
    const diffThisOctober = now.diff(thisOctober, 'days');

    if (diffThisOctober < 0) {
      needsAttentionQuery.filter.from = `${lastOctober.format('YYYY-MM-DD')}`;
      needsAttentionQuery.filter.to = `${lastOctober
        .add(1, 'year')
        .year()}-09-30`;
    } else {
      needsAttentionQuery.filter.from = `${thisOctober.format('YYYY-MM-DD')}`;
      needsAttentionQuery.filter.to = `${thisOctober
        .add(1, 'year')
        .year()}-09-30`;
    }

    return {
      'filter[from]': needsAttentionQuery.filter.from,
      'filter[search]': needsAttentionQuery.filter.search,
      'filter[status]': needsAttentionQuery.filter.status,
      'filter[to]': needsAttentionQuery.filter.to,
      attention: true,
      page: needsAttentionQuery.page,
      perPage: needsAttentionQuery.perPage,
      sort: needsAttentionQuery.sort,
    };
  });

  const lettingSeason = computed(() => {
    if (query.filter.from === null && query.filter.to === null) {
      return 'all';
    }

    return `${query.filter.from}|${query.filter.to}`;
  });

  const needsAttentionRecords = computed(() => {
    if (needsAttentionForms.value.length === 0) {
      return [];
    }

    return needsAttentionForms.value.filter(
      (form) => form.signaturesRemaining !== 0,
    );
  });

  const showHoldingAgreementColumn = computed(() => {
    if (user?.landlord?.holding_agreements_enabled) {
      return true;
    }

    const hasHoldingAgreementRecord = (form: UtilitiesForm) =>
      Boolean(form.includesHoldingAgreement);

    return (
      forms.value.some(hasHoldingAgreementRecord) ||
      needsAttentionForms.value.some(hasHoldingAgreementRecord)
    );
  });

  // Actions
  // eslint-disable-next-line require-await
  async function setDefaultLettingSeason(setAll: boolean) {
    if (setAll) {
      query.filter.from = null;
      query.filter.to = null;
      return;
    }

    const now = dayjs();
    const thisOctober = dayjs().set('month', 9).startOf('month');
    const lastOctober = thisOctober.subtract(1, 'year');

    if (now.isBefore(thisOctober, 'day')) {
      query.filter.from = `${lastOctober.format('YYYY-MM-DD')}`;
      query.filter.to = `${lastOctober.add(1, 'year').year()}-09-30`;
    } else {
      query.filter.from = `${thisOctober.format('YYYY-MM-DD')}`;
      query.filter.to = `${thisOctober.add(1, 'year').year()}-09-30`;
    }
  }

  function resetFilters() {
    Object.assign(query, { ...defaultQuery });
  }

  function sortBy(field: string): void {
    query.sort = query.sort === field ? `-${field}` : field;
  }

  async function getStats() {
    try {
      loading.stats = true;
      const { data } = await utilitiesApi.accounts.stats({
        from: query.filter.from,
        to: query.filter.to,
      });

      if (data.value?.data) {
        Object.assign(stats, data.value.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      loading.stats = false;
    }
  }

  async function getAccounts(dontUpdateRouteQueryParams = false) {
    try {
      loading.table = true;

      if (!updatedFromSalesforce.value) {
        updatedFromSalesforce.value = true;
        await utilitiesApi.accounts.create();
      }

      const { data } = await utilitiesApi.accounts.get(unref(queryParams));

      if (data.value) {
        forms.value = data.value.data.map(mapFormData);
        Object.assign(meta, data.value.meta);
      }

      if (!dontUpdateRouteQueryParams) {
        updateRouteQueryParams({
          query: unref(queryParams),
          options: isInitialLoad.value
            ? {}
            : {
                element: '#utility-orders-table',
                offset: 100,
                smooth: true,
              },
        });
      }

      if (isInitialLoad.value) {
        isInitialLoad.value = false;
      }
    } catch (e) {
      console.error(e);
    } finally {
      loading.table = false;
    }
  }

  async function getNeedsAttentionAccounts() {
    // Disable fetching needs attention table data
    return;

    try {
      needsAttentionLoading.table = true;

      const { data } = await utilitiesApi.accounts.get(
        unref(needsAttentionQueryParams),
      );

      if (data.value) {
        needsAttentionForms.value = data.value.data.map(mapFormData);
        Object.assign(needsAttentionMeta, data.value.meta);
      }
    } catch (e) {
      console.error(e);
    } finally {
      needsAttentionLoading.table = false;
    }
  }

  async function getAccount(id: FormId) {
    try {
      const { data } = await utilitiesApi.accounts.show(id);
      return data.value;
    } catch (e) {
      console.error(e);
    }
  }

  async function deleteAccount(ids: FormId[]) {
    try {
      await utilitiesApi.accounts.destroy({ forms: ids });
    } catch (e) {
      console.error(e);
    }
  }

  async function uploadForms(payload: { forms: FormUploadPayload[] }) {
    try {
      await utilitiesApi.orders.upload(payload);
    } catch (e) {
      console.error(e);
    }
  }

  function openModal(type: string, data = null) {
    if (!MODAL_TYPES.includes(type)) {
      throw new Error(`Invalid modal type: ${type}`);
    }

    if (modal.value.open === type) {
      return;
    }

    modal.value = { open: type, data };
  }

  function closeModal(checkType = '') {
    if (checkType && !modalIsOpen(checkType)) {
      return;
    }

    modal.value = { open: '', data: {} };
  }

  function modalIsOpen(type: string) {
    return modal.value.open === type;
  }

  async function refresh(dontUpdateRouteQueryParams = false) {
    await Promise.all([
      getStats(),
      getAccounts(dontUpdateRouteQueryParams),
      getNeedsAttentionAccounts(),
    ]);
  }

  async function clone(form: any) {
    const holdingAgreementId = form.holdingAgreement?.id;

    if (form.includesHoldingAgreement && holdingAgreementId) {
      const { data, error } =
        await utilitiesApi.holdingAgreements.clone(holdingAgreementId);

      if (error.value?.data?.error) {
        useToaster().add('danger', error.value.data.error);
        return;
      }

      const utilitiesFormId = data.value?.holdingAgreement?.utilities_form_id;
      if (utilitiesFormId) {
        router.push(`/utilities/order/${utilitiesFormId}/details`);
      }
      return;
    }

    const { data } = await utilitiesApi.orders.clone(form.id);
    if (data.value?.id) {
      router.push(`/utilities/order/${data.value.id}/details`);
    }
  }

  async function openPreviewUrl(previewUrl?: string | null) {
    if (!previewUrl) {
      return;
    }

    await navigateTo(previewUrl, {
      external: true,
      open: { target: '_blank' },
    });
  }

  async function preview(form: any) {
    const holdingAgreementId = form.holdingAgreement?.id;

    if (form.includesHoldingAgreement && holdingAgreementId) {
      const { data, error } =
        await utilitiesApi.holdingAgreements.preview(holdingAgreementId);

      if (error.value?.data?.error) {
        useToaster().add('danger', error.value.data.error);
        return;
      }

      await openPreviewUrl(
        data.value?.previewUrl ?? data.value?.preview_url ?? null,
      );
      return;
    }

    const { data } = await utilitiesApi.orders.previewLink(form.id);
    await openPreviewUrl(data.value?.preview_url);
  }

  async function previewUtilitiesForm(form: any) {
    const holdingAgreementId = form.holdingAgreement?.id;

    if (!form.includesHoldingAgreement || !holdingAgreementId) {
      await preview(form);
      return;
    }

    const { data, error } =
      await utilitiesApi.holdingAgreements.previewUtilitiesForm(
        holdingAgreementId,
      );

    if (error.value?.data?.error) {
      useToaster().add('danger', error.value.data.error);
      return;
    }

    await openPreviewUrl(
      data.value?.previewUrl ?? data.value?.preview_url ?? null,
    );
  }

  async function triggerBlobDownload(
    routeName: string,
    routeParams: number | string,
    fallbackFilename: string,
  ) {
    try {
      const toaster = useToaster();
      const loadingToastId = toaster.add(
        'success',
        'Preparing download...',
        10000,
      );

      const result = await useApiFetchBlob(useZiggy(routeName, routeParams));

      toaster.remove(loadingToastId);

      let filename = fallbackFilename;

      const contentDisposition = result.headers.get('Content-Disposition');
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const match = /filename=["']?([^"']+)/.exec(contentDisposition);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toaster.add('success', 'Download started successfully');
    } catch (error) {
      console.error('Download error:', error);
      const toaster = useToaster();
      toaster.add('danger', error.message || 'Failed to download the file');
    }
  }

  async function download(form: any) {
    await triggerBlobDownload(
      'agents.utilities.orders.download',
      form.id,
      form.hashFilename || `utility-order-${form.id}.pdf`,
    );
  }

  async function downloadHoldingAgreement(form: any) {
    const holdingAgreementId = form.holdingAgreement?.id;

    if (!form.includesHoldingAgreement || !holdingAgreementId) {
      useToaster().add('danger', 'Holding agreement not found.');
      return;
    }

    await triggerBlobDownload(
      'agents.utilities.holding-agreements.download',
      holdingAgreementId,
      `holding-agreement-${holdingAgreementId}.pdf`,
    );
  }

  function getGroupSize(form) {
    return form.tenantCount > 0
      ? `${form.tenantCount} (${form.tenantsSigned}/${form.tenantCount})`
      : '-';
  }

  function deleteUtilityForm(formId: number): AsyncData<never, any> {
    return utilitiesApi.orders.delete(formId);
  }

  // Events
  bus.on(async (event, payload) => {
    switch (event) {
      case 'void-order':
        openModal(MODAL_VOID, payload);
        break;
      case 'refresh-table':
        await refresh();
        break;
      case 'clone-order':
        await clone(payload);
        break;
      case 'preview-order':
        await preview(payload);
        break;
      case 'preview-holding-agreement':
        await preview(payload);
        break;
      case 'preview-utility-form':
        await previewUtilitiesForm(payload);
        break;
      case 'download-order':
        await download(payload);
        break;
      case 'download-holding-agreement':
        await downloadHoldingAgreement(payload);
        break;
      case 'send-reminders':
        openModal(MODAL_SEND_REMINDERS, payload);
        break;
      case 'delete-order':
        openModal(MODAL_DELETE, payload);
        break;
    }
  });

  // Watchers
  watch(
    () => query,
    () => {
      getAccounts();
    },
    { deep: true, immediate: false },
  );

  watch(
    () => needsAttentionQuery,
    () => {
      getNeedsAttentionAccounts();
    },
    { deep: true, immediate: false },
  );

  watch([() => query.filter.from, () => query.filter.to], getStats);

  watch(
    () => route.query,
    () => {
      const routeIds = route.query['filter[ids][]'];
      if (
        routeIds &&
        (Array.isArray(routeIds) ? routeIds.length > 0 : routeIds !== '')
      ) {
        Object.assign(query, unref(defaultQuery));
        updateRouteQueryParams({
          query: unref(queryParams),
        });
      }
    },
    { deep: true },
  );

  return {
    // State
    forms,
    needsAttentionForms,
    needsAttentionRecords,
    showHoldingAgreementColumn,
    lettingSeason,
    loading,
    needsAttentionLoading,
    meta,
    needsAttentionMeta,
    modal,
    query,
    needsAttentionQuery,
    queryParams,
    needsAttentionQueryParams,
    stats,
    user,
    // Actions
    closeModal,
    deleteAccount,
    getAccount,
    getAccounts,
    getNeedsAttentionAccounts,
    getStats,
    modalIsOpen,
    openModal,
    refresh,
    resetFilters,
    uploadForms,
    setDefaultLettingSeason,
    sortBy,
    getGroupSize,
    deleteUtilityForm,
  };
});
